/* eslint-disable no-await-in-loop */

import { useAnalytics } from '@newfront-insurance/data-layer-client';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import { useRef, useState } from 'react';

import { UniqueFilenameManager } from './utils/unique-filename-manager';
import { useAdminTRPC } from '../../../shared/providers/trpc';

interface DownloadZipOptions {
  filename?: string;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export function useDownloadZip() {
  const { useMutation } = useAdminTRPC();
  const { mutateAsync } = useMutation('files.downloadFile');
  const [isDownloading, setIsDownloading] = useState(false);
  const [progress, setProgress] = useState(0);
  const isCanceled = useRef(false);
  const analytics = useAnalytics();

  return {
    isDownloading,
    progress,
    cancel: () => {
      isCanceled.current = true;
      setProgress(0);
      setIsDownloading(false);
    },
    downloadZip: async (fileUuids: string[], options: DownloadZipOptions = {}) => {
      const uniqueFilenameManager = UniqueFilenameManager.init();

      // eslint-disable-next-line no-void
      void analytics.track({
        event: 'File - download',
        properties: {
          fileUuids,
        },
      });

      isCanceled.current = false;
      setIsDownloading(true);
      setProgress(0);

      const zip = new JSZip();

      for (let index = 0; index < fileUuids.length; index += 1) {
        const fileUuid = fileUuids[index];

        if (isCanceled.current) {
          setProgress(0);
          options.onCancel?.();
          return;
        }

        const { downloadUrl, file } = await mutateAsync({
          fileUuid,
          preview: false,
        });

        if (downloadUrl) {
          try {
            const fileContents = await fetch(downloadUrl);
            const blob = await fileContents.blob();

            if (file.filename) {
              zip.file(uniqueFilenameManager.getUniqueFilename(file.filename.replaceAll('/', '')), blob, {
                createFolders: false,
                dir: false,
              });
            }
          } catch (err) {
            // invalid/not found file, skipping
          }
        }

        // sets progress percentage based on files processed
        setProgress(Math.floor(((index + 1) / fileUuids.length) * 100));
      }

      if (isCanceled.current) {
        setProgress(0);
        options.onCancel?.();
        return;
      }

      return zip.generateAsync({ type: 'blob' }).then((blob) => {
        saveAs(blob, options.filename ?? 'download.zip');

        setIsDownloading(false);
        setProgress(0);
        options.onSuccess?.();
      });
    },
  };
}
